import { useState } from "react";
import {
  Badge,
  Box,
  Button,
  Center,
  Checkbox,
  Divider,
  Grid,
  GridItem,
  Link,
  Spinner,
  Tag,
  Text,
} from "@chakra-ui/react";
import { colorCodes, format } from "../utils";
import { useDispatch, useSelector } from "react-redux";
import { ExternalLinkIcon } from "@chakra-ui/icons";
import { fetchNext } from "../store/utils";
import { selectArticle, unselectArticle } from "../store/export";

const TruncatedContent = ({ text, record }) => {
  const [show, setShow] = useState(false);

  return (
    <>
      {show ? (
        <Text>
          <div
            dangerouslySetInnerHTML={{
              __html: format(text + " ", "content", record),
            }}
          />
          <Button
            onClick={() => setShow(!show)}
            variant={"link"}
            color={"yellow.500"}
          >
            Read less
          </Button>
        </Text>
      ) : (
        <Text>
          <div
            dangerouslySetInnerHTML={{
              __html: format(
                text.split(" ").slice(0, 50).join(" ") + "...",
                "content",
                record
              ),
            }}
          />
          <Button
            onClick={() => setShow(!show)}
            variant={"link"}
            color={"yellow.500"}
          >
            Read more
          </Button>
        </Text>
      )}
    </>
  );
};

const LoadMoreButton = () => {
  const dispatch = useDispatch();
  const nextPageNum =
    useSelector((state) => state.searchSlice.searchResults).next_page || null;
  const resultsLoading = useSelector(
    (state) => state.searchSlice.loadingResults
  );
  return (
    <>
      {nextPageNum !== null && (
        <Center my={5}>
          <Button
            disabled={resultsLoading}
            onClick={() => dispatch(fetchNext())}
            variant={"link"}
            color={"yellow.500"}
            fontSize={"xl"}
          >
            Load More
          </Button>
        </Center>
      )}
    </>
  );
};

export const Body = () => {
  const searchResults =
    useSelector((state) => state.searchSlice.searchResults).data || [];
  const searchInitiated = useSelector(
    (state) => state.searchSlice.searchInitiated
  );
  const dispatch = useDispatch();
  const selectedArticles = useSelector((s) => s.selectSlice.selectedArticles);

  const loadingResults = useSelector(
    (state) => state.searchSlice.loadingResults
  );
  return (
    <>
      <div>
        {searchResults.map((res) => (
          <>
            <Box w="100%" p={8}>
              <div>
                <Checkbox
                  borderColor={"yellow.500"}
                  value={res.id}
                  isChecked={selectedArticles.includes(res.id)}
                  onChange={(e) => {
                    e.target.checked
                      ? dispatch(
                          selectArticle({
                            id: e.target.value,
                            title: res.title,
                          })
                        )
                      : dispatch(unselectArticle({ id: e.target.value }));
                  }}
                  colorScheme="yellow"
                >
                  <Text> Select for export </Text>
                </Checkbox>
              </div>

              <Link
                isExternal
                href={res.link}
                color="gray.700"
                fontWeight={"bold"}
              >
                <Text as={"span"}>
                  <span
                    dangerouslySetInnerHTML={{
                      __html: format(res.title + " ", "title", res),
                    }}
                  />{" "}
                  <ExternalLinkIcon mx="2px" />
                </Text>
                <Badge
                  ml="1"
                  colorScheme={colorCodes[res.source.toLowerCase()]}
                >
                  {res.source}
                </Badge>
              </Link>
              <br />
              <Text color="gray.700" fontWeight={"bold"}>
                {res.published}
              </Text>
              {res.summary && (
                <Text color="gray.700" mt={2}>
                  <Text as={"span"} fontWeight={"bold"}>
                    Summary:{" "}
                  </Text>
                  <div
                    dangerouslySetInnerHTML={{
                      __html: format(res.summary + " ", "summary", res),
                    }}
                  />
                </Text>
              )}

              {res.snp.length > 0 && (
                <Text mt={2} fontWeight={"bold"}>
                  SnP Details
                </Text>
              )}
              <Grid templateColumns="repeat(5, 1fr)" gap={3}>
                {res.snp.map((r) => (
                  <GridItem mt={2}>
                    <Text fontSize={"sm"}>
                      <Text as={"span"} fontWeight={"bold"}>
                        Company SX and Ticker:{" "}
                      </Text>
                      <span>{r.company_name}</span>
                    </Text>
                    <Text fontSize={"sm"}>
                      <Text as={"span"} fontWeight={"bold"}>
                        Company ID:{" "}
                      </Text>
                      <span>{r.company_id}</span>
                    </Text>
                    <Text fontSize={"sm"}>
                      <Text as={"span"} fontWeight={"bold"}>
                        Project Name:{" "}
                      </Text>
                      <span>{r.project_name}</span>
                    </Text>
                    <Text fontSize={"sm"}>
                      <Text as={"span"} fontWeight={"bold"}>
                        Project ID:{" "}
                      </Text>
                      <span>{r.project_id}</span>
                    </Text>
                    <Text fontSize={"sm"}>
                      <Text as={"span"} fontWeight={"bold"}>
                        Activity Status:{" "}
                      </Text>
                      <span>{r.project_activity_status}</span>
                    </Text>
                    <Text fontSize={"sm"}>
                      <Text as={"span"} fontWeight={"bold"}>
                        Development Stage:{" "}
                      </Text>
                      <span>{r.project_development_stage}</span>
                    </Text>
                    <Text fontSize={"sm"}>
                      <Text as={"span"} fontWeight={"bold"}>
                        Location:{" "}
                      </Text>
                      <span>
                        {r.project_state}, {r.project_country}
                      </span>
                    </Text>
                  </GridItem>
                ))}
              </Grid>
              {res.non_snp_data?.length > 0 && (
                <Text mt={2} fontWeight={"bold"}>
                  Non SNP Details
                </Text>
              )}
              <Grid templateColumns="repeat(5, 1fr)" gap={3}>
                {res.non_snp_data.map((nsd) => (
                  <GridItem mt={2}>
                    <Text fontSize={"sm"}>
                      <Text as={"span"} fontWeight={"bold"}>
                        Company:{" "}
                      </Text>
                      <span>{nsd.company}</span>
                    </Text>
                    <Text fontSize={"sm"}>
                      <Text as={"span"} fontWeight={"bold"}>
                        Location:{" "}
                      </Text>
                      <span>{nsd.location}</span>
                    </Text>
                    <Text fontSize={"sm"}>
                      <Text as={"span"} fontWeight={"bold"}>
                        Location Coordinates:{" "}
                      </Text>
                      <span>{nsd.coordinates}</span>
                    </Text>
                    <Text fontSize={"sm"}>
                      <Text as={"span"} fontWeight={"bold"}>
                        Project Name:{" "}
                      </Text>
                      <span>{nsd.project}</span>
                    </Text>
                    <Text fontSize={"sm"}>
                      <Text as={"span"} fontWeight={"bold"}>
                        Model Confidence:{" "}
                      </Text>
                      <span>{nsd.confidence}</span>
                    </Text>
                    <Text fontSize={"sm"}>
                      <Text as={"span"} fontWeight={"bold"}>
                        Sentence:{" "}
                      </Text>
                      <span>{nsd.sentence}</span>
                    </Text>
                  </GridItem>
                ))}
              </Grid>
              {res.chatgpt_summary && (
                <Text color="gray.700" mt={5}>
                  <Text fontWeight={"bold"}>ChatGPT Summary</Text>
                  <Text>{res.chatgpt_summary}</Text>
                </Text>
              )}
              <Text color="gray.700" mt={5}>
                <Text as={"span"} fontWeight={"bold"}>
                  Article Text
                </Text>
                {res.page_num !== 0 && (
                  <Text as={"span"}> (Page: {res.page_num})</Text>
                )}
                <Text sx={{ "white-space": "pre-wrap", "line-height": "150%" }}>
                  {res.content.length > 250 ? (
                    <TruncatedContent record={res} text={res.content} />
                  ) : (
                    res.content
                  )}
                </Text>
              </Text>
            </Box>
            <Divider />
          </>
        ))}
        <LoadMoreButton />
        {searchResults.length === 0 && searchInitiated && !loadingResults && (
          <Center>
            <Text mt={20} fontSize={"xl"}>
              No results found
            </Text>
          </Center>
        )}
        {searchResults.length === 0 && searchInitiated && loadingResults && (
          <Center mt={20}>
            <Spinner
              thickness="4px"
              speed="0.65s"
              emptyColor="gray.200"
              color="yellow.500"
              size="xl"
            />
          </Center>
        )}
        {searchResults.length === 0 && !searchInitiated && (
          <Center>
            <Text mt={20} fontSize={"xl"}>
              Search for keywords or phrases
            </Text>
          </Center>
        )}
      </div>
    </>
  );
};
